"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { X } from "lucide-react";

import { useLanguage } from "../i18n/LanguageProvider";
import {
  PENGUMUMAN_BPJS as P,
  ambilTeks,
  kodeBahasa,
  sudahBerlaku,
  stripMasihTayang,
} from "../data/pengumuman";

/* ============================================================
   STRIP PENGUMUMAN

   Satu baris tipis di puncak beranda. Isinya satu kalimat dan satu
   tautan - bukan poster, bukan kartu. Tugasnya cuma memberi tahu
   bahwa ada sesuatu yang berubah; penjelasannya ada di halaman
   detail.

   ============================================================
   KENAPA ADA `siap`, DAN KENAPA INI BUKAN KERUMITAN YANG BISA
   DIHAPUS

   Komponen ini bergantung pada dua hal yang HANYA ADA DI BROWSER:
     - tanggal hari ini (menentukan kalimat mana yang dipakai)
     - localStorage (apakah pengunjung sudah menutupnya)

   Peladen tidak tahu keduanya. Kalau strip dirender langsung, HTML
   dari peladen berbeda dari yang dihitung React di browser, dan
   React melaporkannya sebagai hydration error - persis masalah yang
   dijelaskan panjang lebar di layout.js soal kelas `dark`.

   Bedanya dengan kasus di layout.js: di sana perbedaannya SENGAJA
   dipertahankan dan dibungkam dengan suppressHydrationWarning,
   karena mencegah kedipan lebih penting. Di sini tidak ada yang
   perlu dipertahankan - strip yang muncul terlambat 50ms tidak
   merugikan siapa pun. Jadi cara yang benar adalah tidak merender
   apa pun sampai menempel, bukan membungkam peringatannya.

   ⚠️ JANGAN ganti `siap` dengan suppressHydrationWarning di sini.
   Atribut itu hanya mendiamkan peringatan; isi yang salah tetap
   terkirim ke pengunjung.
   ============================================================ */

/* Kunci localStorage. Awalan `rsjmc-` disamakan dengan `rsjmc-theme`
   di layout.js - satu situs, satu awalan, supaya gampang dibersihkan
   dan tidak bentrok dengan aplikasi lain di domain yang sama. */
const KUNCI = `rsjmc-pengumuman-${P.id}`;

export default function StripPengumuman() {
  const ctx = useLanguage();
  const kode = kodeBahasa(ctx);

  const [siap, setSiap] = useState(false);
  const [tampil, setTampil] = useState(false);
  const [berlaku, setBerlaku] = useState(false);

  useEffect(() => {
    const sekarang = new Date();

    /* Lewat masa tayang - tidak usah menyentuh localStorage sama
       sekali. */
    if (!stripMasihTayang(sekarang)) {
      setSiap(true);
      return;
    }

    let ditutup = false;
    try {
      ditutup = localStorage.getItem(KUNCI) === "tutup";
    } catch (e) {
      /* Mode penyamaran melempar error di sini. Kalau itu terjadi,
         anggap saja belum pernah ditutup: strip tampil terus.
         Mengganggu sedikit, jauh lebih baik daripada pengumuman yang
         hilang. */
    }

    setBerlaku(sudahBerlaku(sekarang));
    setTampil(!ditutup);
    setSiap(true);
  }, []);

  function tutup() {
    setTampil(false);
    try {
      localStorage.setItem(KUNCI, "tutup");
    } catch (e) {}
  }

  if (!siap || !tampil) return null;

  const kalimat = ambilTeks(
    berlaku ? P.strip.sesudahMulai : P.strip.sebelumMulai,
    kode
  );

  return (
    /* role="region" + aria-label: pembaca layar mengumumkan ini
       sebagai bagian tersendiri, bukan teks nyasar sebelum header
       halaman.

       TIDAK memakai role="alert". role="alert" memotong apa pun yang
       sedang dibacakan pembaca layar - pantas untuk kesalahan yang
       baru saja terjadi, berlebihan untuk pengumuman yang sudah ada
       sejak halaman dibuka. */
    <div
      role="region"
      aria-label={ambilTeks(P.judul.sebelumMulai, kode)}
      className="w-full bg-[#0f6a3b] text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-6 md:px-6">

        <p className="min-w-0 flex-1 text-[13px] leading-snug sm:text-sm">
          {kalimat}{" "}
          {/* Tautan ikut di dalam <p>, bukan di sebelahnya. Di layar
              sempit kalimat dan tautan jadi satu blok yang membungkus
              wajar; kalau dipisah jadi dua elemen sejajar, tautannya
              terdorong ke baris sendiri dan strip jadi dua baris
              penuh. */}
          <Link
            href={P.tautan}
            className="whitespace-nowrap font-semibold underline underline-offset-2 transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {ambilTeks(P.strip.tautanTeks, kode)}
          </Link>
        </p>

        {/* Tombol tutup. 44x44 area sentuh minimum - lebih kecil dari
            itu dan pengguna HP meleset ke tautan di sebelahnya, yang
            artinya mereka malah berpindah halaman saat bermaksud
            menutup. */}
        <button
          type="button"
          onClick={tutup}
          aria-label={ambilTeks(P.strip.tutup, kode)}
          className="-mr-2 grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

      </div>
    </div>
  );
}
